import { api } from './api';

export interface ComplianceChecklist {
  _id: string;
  name: string;
  slug: string;
  type: string;
  checklist: string[];
  document: string;
  asset?: string;
  frequency?: string;
  nextDueDate?: string;
  status?: string;
  createdAt: string;
  updatedAt: string;
  __v?: number;
}

export interface ComplianceTypeFormData {
  name: string;
  description?: string;
}

export interface ComplianceChecklistResponse {
  message: string;
  data: {
    result: ComplianceChecklist[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
    previousPage: number | null;
    nextPage: number | null;
  };
  error: boolean;
}

export interface ComplianceType {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
}

export interface ComplianceTypeResponse {
  message: string;
  data: {
    result: ComplianceType[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
    previousPage: number | null;
    nextPage: number | null;
  };
  error: boolean;
}

// --- Asset Compliance Types ---
export interface AssetCompliance {
  _id: string;
  checklist: string;
  status: string;
  asset: string;
  owner: string;
  nextDate: string;
  isRecurring: boolean;
  frequency: string;
  createdAt: string;
  updatedAt: string;
}

export interface AssetComplianceResponse {
  message: string;
  data: {
    result: AssetCompliance[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
    previousPage: number | null;
    nextPage: number | null;
  };
  error: boolean;
}

// --- Maintenance Compliance Types ---
export interface MaintenanceCompliance {
  _id: string;
  checklist: string;
  maintenance: string;
  complianceOfficer: string;
  status: string;
  priority: string;
  scheduledDate: string;
  createdAt: string;
  updatedAt: string;
  __v?: number;
}

export interface MaintenanceComplianceAPIResponse {
  message: string;
  data: {
    result: MaintenanceCompliance[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
    previousPage: number | null;
    nextPage: number | null;
  };
  error: boolean;
}

// For table row
export interface MaintenanceComplianceTableRow {
  _id: string;
  assetName: string;
  description: string;
  maintenanceType: string;
  priority: string;
  status: string;
  scheduledDate: string;
  checklist: string;
}

export const complianceService = {
  getComplianceChecklists: async () => {
    const response = await api.get('/compliance-checklist');
    return response.data as ComplianceChecklistResponse;
  },
  getComplianceTypes: async () => {
    const response = await api.get('/compliance-type');
    return response.data as ComplianceTypeResponse;
  },
  createComplianceType: async (data: { name: string; description?: string }) => {
    const response = await api.post('/compliance-type', data);
    return response.data;
  },
  updateComplianceType: async (id: string, data: { name: string; description?: string }) => {
    const response = await api.put(`/compliance-type/${id}`, data);
    return response.data;
  },
  deleteComplianceType: async (id: string) => {
    const response = await api.delete(`/compliance-type/${id}`);
    return response.data;
  },

  // --- Asset Compliance Service Methods ---
  getAssetCompliances: async (page = 1, limit = 10) => {
    const response = await api.get<AssetComplianceResponse>(`/asset-compliance?page=${page}&limit=${limit}`);
    return response.data;
  },
  getAssetCompliance: async (id: string) => {
    const response = await api.get<{ message: string; data: AssetCompliance; error: boolean }>(`/asset-compliance/${id}`);
    return response.data.data;
  },
  updateAssetCompliance: async (id: string, data: Partial<AssetCompliance>) => {
    const response = await api.put(`/asset-compliance/${id}`, data);
    return response.data;
  },
  deleteAssetCompliance: async (id: string) => {
    const response = await api.delete(`/asset-compliance/${id}`);
    return response.data;
  },

  // Maintenance Compliance
  getMaintenanceCompliances: async (page = 1, limit = 10): Promise<MaintenanceComplianceAPIResponse> => {
    const response = await api.get(`/maintenance-compliance?page=${page}&limit=${limit}`);
    return response.data;
  },

  // Fetch a single maintenance compliance (if needed)
  getMaintenanceCompliance: async (id: string): Promise<MaintenanceCompliance> => {
    const response = await api.get(`/maintenance-compliance/${id}`);
    return response.data.data;
  },
};